/*!

=========================================================
* Argon Dashboard React - v1.2.4
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import { useEffect, useState, useMemo } from "react";

import classnames from "classnames";

import Chart from "chart.js";

import { Line, Bar } from "react-chartjs-2";

import {
  Card,
  CardHeader,
  CardBody,
  NavItem,
  NavLink,
  Nav,
  Progress,
  Table,
  Container,
  Row,
  Col,
} from "reactstrap";

import {
  chartOptions,
  parseOptions,
} from "variables/charts.js";

import Header from "components/Headers/Header.js";
import { provincesRepo } from "data/repositories/provincesRepo";
import { dealsRepo } from "data/repositories/dealsRepo";
import { businessesRepo } from "data/repositories/businessesRepo";
import { bookingsRepo } from "data/repositories/bookingsRepo";
import { subscriptionsRepo } from "data/repositories/subscriptionsRepo";
import { usersRepo } from "data/repositories/usersRepo";
import { alertsRepo } from "data/repositories/alertsRepo";
import { favoritesRepo } from "data/repositories/favoritesRepo";

const Index = () => {
  const [activeNav, setActiveNav] = useState(1);

  const [provinces, setProvinces] = useState([]);
  const [deals, setDeals] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [users, setUsers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [favorites, setFavorites] = useState({ provinceIds: [], dealIds: [] });

  useEffect(() => {
    if (window.Chart) {
      parseOptions(Chart, chartOptions());
    }
    setProvinces(provincesRepo.list());
    setDeals(dealsRepo.list());
    setBusinesses(businessesRepo.list());
    setBookings(bookingsRepo.list());
    setSubscriptions(subscriptionsRepo.list());
    setUsers(usersRepo.list());
    setAlerts(alertsRepo.list());
    setFavorites(favoritesRepo.get());
  }, []);

  const headerStats = useMemo(() => [
    { title: "İller", value: provinces.length, icon: "fas fa-map-marker-alt", color: "bg-info", detail: "Keşif kataloğu" },
    { title: "İşletmeler", value: businesses.length, icon: "fas fa-store", color: "bg-success", detail: `${businesses.filter((b) => !b.isDeleted).length} aktif` },
    { title: "Rezervasyonlar", value: bookings.length, icon: "fas fa-calendar-check", color: "bg-warning", detail: `${bookings.filter((b) => b.status === "pending").length} bekleyen` },
    { title: "Kullanıcılar", value: users.length, icon: "fas fa-users", color: "bg-danger", detail: `${users.filter((u) => u.role === "admin").length} admin` },
  ], [provinces, businesses, bookings, users]);

  // İl bazlı işletme dağılımı (bar chart)
  const provinceBusinessCounts = useMemo(() => {
    const map = {};
    businesses.forEach((b) => {
      const pName = provinces.find((p) => p.id === b.provinceId)?.name || b.provinceId;
      map[pName] = (map[pName] || 0) + 1;
    });
    const sorted = Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 8);
    return {
      labels: sorted.map(([name]) => name),
      values: sorted.map(([, count]) => count),
    };
  }, [provinces, businesses]);

  // İl bazlı rezervasyon dağılımı (line chart)
  const provinceBookingCounts = useMemo(() => {
    const map = {};
    bookings.forEach((bk) => {
      const biz = businesses.find((b) => b.id === bk.businessId);
      const pName = biz ? (provinces.find((p) => p.id === biz.provinceId)?.name || biz.provinceId) : "Bilinmeyen";
      map[pName] = (map[pName] || 0) + 1;
    });
    const sorted = Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 8);
    return {
      labels: sorted.map(([name]) => name),
      values: sorted.map(([, count]) => count),
    };
  }, [provinces, businesses, bookings]);

  const lineChartData = {
    labels: provinceBookingCounts.labels,
    datasets: [
      {
        label: "Rezervasyon",
        data: provinceBookingCounts.values,
        borderColor: "#5e72e4",
        backgroundColor: "rgba(94,114,228,0.1)",
        pointBackgroundColor: "#5e72e4",
        fill: true,
      },
    ],
  };
  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      yAxes: [{ ticks: { beginAtZero: true, stepSize: 1 } }],
    },
    tooltips: { intersect: false },
  };

  const barChartData = {
    labels: provinceBusinessCounts.labels,
    datasets: [
      {
        label: "İşletme",
        data: provinceBusinessCounts.values,
        backgroundColor: "#fb6340",
        maxBarThickness: 20,
      },
    ],
  };
  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      yAxes: [{ ticks: { beginAtZero: true, stepSize: 1 } }],
    },
    tooltips: { intersect: false },
  };

  // İl bazlı istatistik tablosu
  const provinceStats = useMemo(() => {
    return provinces.map((p) => {
      const bizCount = businesses.filter((b) => b.provinceId === p.id).length;
      const dealCount = deals.filter((d) => d.provinceId === p.id).length;
      const bookCount = bookings.filter((bk) => {
        const biz = businesses.find((b) => b.id === bk.businessId);
        return biz && biz.provinceId === p.id;
      }).length;
      return { name: p.name, businesses: bizCount, deals: dealCount, bookings: bookCount, highlights: p.highlights?.length || 0 };
    }).sort((a, b) => b.businesses - a.businesses);
  }, [provinces, businesses, deals, bookings]);

  // Tema dağılımı
  const themeDistribution = useMemo(() => {
    const map = {};
    deals.forEach((d) => {
      map[d.theme] = (map[d.theme] || 0) + 1;
    });
    businesses.forEach((b) => {
      if (b.category) map[b.category] = (map[b.category] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [deals, businesses]);

  const totalThemeCount = themeDistribution.reduce((s, [, v]) => s + v, 0) || 1;

  const toggleNavs = (e, index) => {
    e.preventDefault();
    setActiveNav(index);
  };

  return (
    <>
      <Header stats={headerStats} />
      <Container className="mt--7" fluid>
        <Row>
          <Col className="mb-5 mb-xl-0" xl="8">
            <Card className="bg-gradient-default shadow">
              <CardHeader className="bg-transparent">
                <Row className="align-items-center">
                  <div className="col">
                    <h6 className="text-uppercase text-light ls-1 mb-1">
                      Genel bakış
                    </h6>
                    <h2 className="text-white mb-0">
                      {activeNav === 1 ? "İl bazlı rezervasyonlar" : "Fırsat & abonelik"}
                    </h2>
                  </div>
                  <div className="col">
                    <Nav className="justify-content-end" pills>
                      <NavItem>
                        <NavLink
                          className={classnames("py-2 px-3", { active: activeNav === 1 })}
                          href="#"
                          onClick={(e) => toggleNavs(e, 1)}
                        >
                          <span className="d-none d-md-block">Rezervasyon</span>
                          <span className="d-md-none">R</span>
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames("py-2 px-3", { active: activeNav === 2 })}
                          href="#"
                          onClick={(e) => toggleNavs(e, 2)}
                        >
                          <span className="d-none d-md-block">Özet</span>
                          <span className="d-md-none">Ö</span>
                        </NavLink>
                      </NavItem>
                    </Nav>
                  </div>
                </Row>
              </CardHeader>
              <CardBody>
                <div className="chart">
                  {activeNav === 1 ? (
                    <Line data={lineChartData} options={lineOptions} />
                  ) : (
                    <Table className="align-items-center" responsive dark borderless size="sm">
                      <tbody>
                        <tr><td className="text-white">Toplam fırsat</td><td className="text-white font-weight-bold">{deals.length}</td></tr>
                        <tr><td className="text-white">Aktif abonelik</td><td className="text-white font-weight-bold">{subscriptions.filter((s) => s.status === "active").length}</td></tr>
                        <tr><td className="text-white">Uyarı kuralı</td><td className="text-white font-weight-bold">{alerts.length}</td></tr>
                        <tr><td className="text-white">Kaydedilen il</td><td className="text-white font-weight-bold">{favorites.provinceIds.length}</td></tr>
                        <tr><td className="text-white">Kaydedilen fırsat</td><td className="text-white font-weight-bold">{favorites.dealIds.length}</td></tr>
                      </tbody>
                    </Table>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col xl="4">
            <Card className="shadow">
              <CardHeader className="bg-transparent">
                <Row className="align-items-center">
                  <div className="col">
                    <h6 className="text-uppercase text-muted ls-1 mb-1">
                      Dağılım
                    </h6>
                    <h2 className="mb-0">İl bazlı işletmeler</h2>
                  </div>
                </Row>
              </CardHeader>
              <CardBody>
                <div className="chart">
                  <Bar data={barChartData} options={barOptions} />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row className="mt-5">
          <Col className="mb-5 mb-xl-0" xl="8">
            <Card className="shadow">
              <CardHeader className="border-0">
                <Row className="align-items-center">
                  <div className="col">
                    <h3 className="mb-0">İl istatistikleri</h3>
                  </div>
                </Row>
              </CardHeader>
              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th scope="col">İl</th>
                    <th scope="col">İşletme</th>
                    <th scope="col">Fırsat</th>
                    <th scope="col">Rezervasyon</th>
                    <th scope="col">Öne çıkan</th>
                  </tr>
                </thead>
                <tbody>
                  {provinceStats.slice(0, 8).map((row) => (
                    <tr key={row.name}>
                      <th scope="row">{row.name}</th>
                      <td>{row.businesses}</td>
                      <td>{row.deals}</td>
                      <td>{row.bookings}</td>
                      <td>{row.highlights}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          </Col>
          <Col xl="4">
            <Card className="shadow">
              <CardHeader className="border-0">
                <Row className="align-items-center">
                  <div className="col">
                    <h3 className="mb-0">Tema dağılımı</h3>
                  </div>
                </Row>
              </CardHeader>
              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th scope="col">Tema</th>
                    <th scope="col">Sayı</th>
                    <th scope="col" />
                  </tr>
                </thead>
                <tbody>
                  {themeDistribution.slice(0, 6).map(([theme, count]) => {
                    const pct = Math.round((count / totalThemeCount) * 100);
                    return (
                      <tr key={theme}>
                        <th scope="row">{theme}</th>
                        <td>{count}</td>
                        <td>
                          <div className="d-flex align-items-center">
                            <span className="mr-2">{pct}%</span>
                            <div>
                              <Progress max="100" value={pct} barClassName="bg-gradient-info" />
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Index;
